import { wallManager } from '../wallManager';
import { BaseShape } from '../IShape';
import { Vector2 } from '@babylonjs/core';
import Element, { ElementType, SpecyficType } from '../element';
import store from '@/store';
import { WallModel } from '../wallModel';
import { BaseCommand } from './baseCommand';
import { messager } from '../messager';
import { HandleBacksCommand } from './handleBacksCommand';
import { HandleLegsSupportCommand } from './handleLegsSupportCommand';

export class AddWallsCommand extends BaseCommand {

    private static cachedWallModels: WallModel[] = [];

    private removedElements: Element[] = [];

    constructor(
        private element: Element,
        scenePreview: any,
        private onFinish: () => void = () => { },
    ) {
        super(scenePreview);
    }

    public execute(): void {
        const wallModels = this.getWallModels();
        AddWallsCommand.cachedWallModels.push(...wallModels);
        const supportsAndShelves = this.scenePreview.elements
            .filter((x: Element) =>
                x.type === ElementType.WALL_SUPPORT ||
                x.specyficType === SpecyficType.SHELF,
            );
        if (wallModels.length === 0) { this.onFinish(); }
        wallModels.forEach((x) => {
            const shader = x.isSupport ?
                store.getters.shaders.selectedSupport :
                store.getters.shaders.selectedWall;
            this.scenePreview.addElement(x.model, x.light, shader, x.position, x.shape, (wall: Element) => {
                this.elements.push(wall);
                const elementsToRemove = this.getElementsToRemove(x, supportsAndShelves);
                elementsToRemove.forEach((element) => {
                    this.removeElement(element);
                });
                AddWallsCommand.cachedWallModels.splice(
                    AddWallsCommand.cachedWallModels.indexOf(x),
                    1,
                );
                if (this.elements.length === wallModels.length) {
                    const handleBacksCommand = new HandleBacksCommand(this.scenePreview);
                    this.addCommand(handleBacksCommand);
                    this.handleSnackbar(wallModels);
                    this.handleLegsSupport();
                    this.onFinish();
                }
            });
        });
    }

    public revert(): void {
        this.removeElements();
        this.revertCommands();
        this.removedElements.forEach((x) => {
            x.isVisible = true;
            this.scenePreview.elements.push(x);
        });
    }

    private handleSnackbar(wallModels: WallModel[]) {
        if (wallModels.some((m) => m.isSupport && !this.wasReplaced(m))) {
            const text = this.scenePreview.$t('addedSupport').toString();
            messager.info(text);
        }
        if (wallModels.some((m) => !m.isSupport)) {
            const text = this.scenePreview.$t('addedWall').toString();
            messager.info(text);
        }
    }

    private handleLegsSupport() {
        const command = new HandleLegsSupportCommand(this.scenePreview);
        this.addCommand(command);
    }

    private wasReplaced(m: WallModel): boolean {
        return (this.removedElements.some((element) =>
            element.position.x === m.position.x &&
            element.position.y === m.position.y &&
            element.equal(m) &&
            element.type === ElementType.WALL_SUPPORT,
        ));
    }

    private getWallModels(): WallModel[] {
        return wallManager
            .getWallModels(this.element)
            .filter((wall) => {
                return !AddWallsCommand.cachedWallModels.some((x) => {
                    return wall.inside(x);
                });
            });
    }

    private getElementsToRemove(wallModel: WallModel, elements: Element[]): Element[] {
        const testShape = new BaseShape(
            new Vector2(
                wallModel.position.x,
                wallModel.position.y + wallModel.shape.y / 2,
            ),
            Vector2.Zero(),
        );
        return elements.filter((element: Element) =>
            testShape.inside(element) &&
            !this.removedElements.includes(element),
        );
    }

    private removeElement(element: Element) {
        this.removedElements.push(element);
        this.scenePreview.elements.splice(
            this.scenePreview.elements.indexOf(element),
            1,
        );
        element.isVisible = false;
    }
}
